// apps/web/src/features/cargo/components/CargoModalHost.tsx
//
// Renders exactly one Cargo workflow modal at a time from the workflow state
// (#1232): add / edit / import / payment / delete. Keeping the switch here
// keeps `CargoRegistryPage` focused on layout + data wiring.

import type { CargoManagerOption } from '../forms/useCargoFormOptions.js';
import type { CargoWorkflows } from '../useCargoWorkflows.js';
import { CargoAddModal } from './CargoAddModal.js';
import { CargoEditModal } from './CargoEditModal.js';
import { CargoImportModal } from './CargoImportModal.js';
import { CargoPaymentDrawer } from './CargoPaymentDrawer.js';
import { CargoDeleteConfirm } from './CargoDeleteConfirm.js';
import { CargoReadOnlyModal } from './CargoReadOnlyModal.js';

export interface CargoModalHostProps {
  workflows: CargoWorkflows;
  managerOptions: readonly CargoManagerOption[];
}

export function CargoModalHost({ workflows, managerOptions }: CargoModalHostProps) {
  const { modal, closeModal } = workflows;
  if (!modal) return null;

  switch (modal.kind) {
    case 'add':
      return <CargoAddModal managerOptions={managerOptions} onClose={closeModal} />;
    case 'import':
      return <CargoImportModal onClose={closeModal} />;
    case 'edit':
      return modal.row ? (
        <CargoEditModal row={modal.row} managerOptions={managerOptions} onClose={closeModal} />
      ) : null;
    case 'view':
      return modal.row ? <CargoReadOnlyModal row={modal.row} onClose={closeModal} /> : null;
    case 'payment':
      return modal.row ? (
        <CargoPaymentDrawer
          row={modal.row}
          onClose={closeModal}
          readOnlyMirror={workflows.readOnlyMirror}
        />
      ) : null;
    case 'delete':
      return modal.row ? <CargoDeleteConfirm row={modal.row} onClose={closeModal} /> : null;
    default:
      return null;
  }
}
